import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Award, CheckCircle, XCircle, User, BookOpen, Clock,
  Calendar, Shield, Download, Loader2, ArrowLeft
} from 'lucide-react';
import { LogoUpim } from '../components/ui-upim';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyCertificatePage() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyCertificate();
  }, [code]);

  const verifyCertificate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/educational/certificates/verify/${code}`);
      setIsValid(response.data.valid);
      setCertificate(response.data.certificate);
    } catch (error) {
      setError('Error al verificar el certificado');
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Verificando certificado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 to-purple-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-indigo-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
          <div className="flex items-center gap-4">
            <LogoUpim className="h-14" claseTexto="text-2xl" testId="verificar-logo" />
            <div>
              <h1 className="text-2xl font-bold">Verificación de Certificado</h1>
              <p className="text-indigo-200">Sistema de autenticación de certificados UPIM</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {isValid && certificate ? (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Success Banner */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-full">
                  <CheckCircle className="w-10 h-10" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Certificado Válido</h2>
                  <p className="text-green-100">Este certificado ha sido verificado y es auténtico</p>
                </div>
              </div>
            </div>

            {/* Certificate Details */}
            <div className="p-8">
              {/* Holder Name */}
              <div className="text-center mb-8">
                <p className="text-gray-500 mb-2">Otorgado a</p>
                <h3 className="text-3xl font-bold text-gray-900">{certificate.holder_name}</h3>
                <div className="w-24 h-1 bg-amber-500 mx-auto mt-4"></div>
              </div>

              {/* Content Info */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <p className="text-gray-500 text-sm mb-2">Por completar exitosamente</p>
                <h4 className="text-xl font-bold text-gray-900 mb-4">{certificate.content_title}</h4>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-indigo-500" />
                    <div>
                      <p className="text-xs text-gray-500">Tipo</p>
                      <p className="font-medium text-gray-900 capitalize">{certificate.content_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-indigo-500" />
                    <div>
                      <p className="text-xs text-gray-500">Instructor</p>
                      <p className="font-medium text-gray-900">{certificate.speaker}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-indigo-500" />
                    <div>
                      <p className="text-xs text-gray-500">Duración</p>
                      <p className="font-medium text-gray-900">{certificate.duration_hours?.toFixed(1)} hrs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-indigo-500" />
                    <div>
                      <p className="text-xs text-gray-500">Fecha</p>
                      <p className="font-medium text-gray-900">
                        {certificate.issued_at ? new Date(certificate.issued_at).toLocaleDateString('es-MX') : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verification Code */}
              <div className="flex items-center justify-between bg-indigo-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-indigo-600" />
                  <div>
                    <p className="text-xs text-indigo-600">Código de verificación</p>
                    <p className="font-mono font-bold text-indigo-900">{certificate.verification_code}</p>
                  </div>
                </div>
                <a
                  href={`${API_URL}/api/educational/certificates/download/${certificate.verification_code}`}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </a>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-8 py-4 text-center text-sm text-gray-500">
              Certificado emitido por UPIM - Unión de Profesionales Inmobiliarios de México
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Error Banner */}
            <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-full">
                  <XCircle className="w-10 h-10" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Certificado No Válido</h2>
                  <p className="text-red-100">No se encontró ningún certificado con este código</p>
                </div>
              </div>
            </div>

            <div className="p-8 text-center">
              <p className="text-gray-600 mb-6">
                El código de verificación <span className="font-mono font-bold">{code}</span> no corresponde a ningún certificado registrado en nuestro sistema.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Si crees que esto es un error, por favor contacta a soporte.
              </p>
              <Link
                to="/masterclasses"
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Ver cursos disponibles
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
